/**
 * Dom-less render-plugin identity and binding model.
 *
 * This is the shared rows-side core that DOM and CLI adapters can
 * both consume. It deliberately stops at row identity, plugin ids,
 * roles, aliases, and binding decisions; DOM cells, legends, row
 * options, and terminal labels live in adapter layers.
 */

import type { Option } from "../core/option.ts";
import { Json, type JsonObject } from "../core/json.ts";
import type { Descriptor, RetiredRendererType, Role } from "../rows/descriptor.ts";
import type { PluginDataProviders } from "../providers/plugin-providers.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { ShapeKey } from "../core/shape.ts";
import type { RendererId } from "../rows/slot.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { AliasRow } from "../rows/variants/alias-row.ts";
import { InertRow } from "../rows/variants/inert-row.ts";
import { LayoutRow } from "../rows/variants/layout-row.ts";
import { RendererRow } from "../rows/variants/renderer-row.ts";
import { SourceRow } from "../rows/variants/source-row.ts";
import { elementAt } from "../core/checked-index.ts";
import { deepFrozen } from "../core/frozen-collections.ts";

/** Which render surface is being resolved for a row. */
export type RenderBindingSurface = "row" | "cell" | "chrome";

/** Why a row resolved to a particular render plugin. */
export type RenderBindingKind =
  | "row-descriptor"
  | "source-default-renderer"
  | "source-renderer-override"
  | "source-fallback"
  | "renderer-row"
  | "alias-template"
  | "alias-cell-descendant"
  | "layout-row";

/** Role-qualified plugin identity. */
export interface RenderPluginRef {
  readonly id: string;
  readonly role: Role;
}

/**
 * What a render plugin does when a row's cells are not the shape it accepts (`ignore`, `warn` or
 * `error`), declared in its accepted shape.
 */
export type ShapeMismatchPolicy = "ignore" | "warn" | "error";

/** A cell shape a render plugin accepts, and what to do when a cell does not match it. */
export interface RenderPluginAcceptedShape<C = unknown> {
  readonly shape: ShapeKey<C>;
  readonly onMismatch: ShapeMismatchPolicy;
}

/**
 * A render plugin's shape-gate verdict for one row: accepted, or refused with the actual and
 * expected shapes and the plugin's mismatch policy.
 */
export type ShapeGateDecision =
  | { readonly accepted: true }
  | {
      readonly accepted: false;
      readonly actual: Option<string>;
      readonly expected: readonly string[];
      readonly onMismatch: ShapeMismatchPolicy;
    };

/** Dom-less render plugin identity data. DOM/CLI adapters extend this. */
export interface RenderPluginSpec {
  readonly id: string;
  readonly role: Role;
  readonly aliases?: readonly string[];
  /**
   * Object-cell payload shapes this plugin knows how to render.
   * This is a consumer contract, not a source cardinality declaration.
   */
  readonly accepts?: readonly RenderPluginAcceptedShape[];
  /**
   * Optional pure data providers attached to this canonical plugin id.
   * DOM and CLI adapters both resolve providers through the spec, so
   * pure data flows (item lists, fixed-child intent, subcolumn
   * counts) stay aligned across adapters.
   *
   * The factory shape lets the catalog defer construction until an
   * adapter actually needs the provider.
   */
  readonly dataProvider?: () => PluginDataProviders;
}

function checkRenderPluginShape(
  spec: RenderPluginSpec,
  actualShape: Option<string>,
): ShapeGateDecision {
  if (!spec.accepts || spec.accepts.length === 0) return { accepted: true };
  if (!actualShape) return { accepted: true };
  for (const accepted of spec.accepts) {
    if (accepted.shape.id === actualShape) return { accepted: true };
  }
  return {
    accepted: false,
    actual: actualShape,
    expected: spec.accepts.map((accepted) => accepted.shape.id),
    onMismatch: elementAt(spec.accepts, 0, "checkRenderPluginShape: spec.accepts").onMismatch,
  };
}

/** Canonical renderer ids for the canvas slot. */
export const CANVAS_RENDERER_PLUGIN_IDS = [
  "canvas.hrule",
  "canvas.points",
  "canvas.line",
  "canvas.bar",
  "canvas.regression-curve",
  "canvas.regression-band",
] as const;
deepFrozen(CANVAS_RENDERER_PLUGIN_IDS);

/**
 * One of the canvas painter ids (`CANVAS_RENDERER_PLUGIN_IDS`), which a canvas layout dispatches
 * its painter children on.
 */
export type CanvasRendererPluginId =
  (typeof CANVAS_RENDERER_PLUGIN_IDS)[number];

/**
 * The mark kind a data-renderer row draws — `"line"`, `"hrule"`,
 * `"regression-curve"`, … — resolved from the row's canonical wire type,
 * falling back to its legacy `attributes.type`.
 *
 * A self-describing mark carries its identity in the qualified wire `_type`
 * (`canvas.line`, `timeline[column].points`), so the kind is the segment after
 * the final `.`. Older rows spell the wire type `data-renderer` and put the
 * kind in the attribute bag; those are the fallback, not the contract.
 *
 * Returns null when neither source names a kind — callers treat that as "no
 * renderer for this row" rather than as an error, and `createSeriesRenderer`
 * likewise returns null for a kind it does not know.
 *
 * Distinct from `renderer-kind.ts`'s `rendererSlotKindOf`, which parses the id
 * against the known slots and also honours `attributes.rendererId`.
 */
export function rendererMarkKindOf(
  wireType: Option<string>,
  attrs: Option<JsonObject>,
): Option<string> {
  // `timeline[column].points` contains a `.` only as the slot separator, so
  // the LAST dot is the right split — an earlier one would return "column]".
  if (wireType != null) {
    const dot = wireType.lastIndexOf(".");
    if (dot >= 0) {
      const kind = wireType.slice(dot + 1);
      if (kind.length > 0) return kind;
    }
  }
  const legacy = Json.string(attrs, "type");
  return legacy != null && legacy.length > 0 ? legacy : null;
}

/** Canonical renderer ids for task rows across sheet slots. */
export const TASK_RENDERER_PLUGIN_IDS = [
  "column.task",
  "timeline[row].task",
  "timeline[column].task",
  "grid.task",
] as const;
deepFrozen(TASK_RENDERER_PLUGIN_IDS);

/**
 * One of the task renderer ids (`TASK_RENDERER_PLUGIN_IDS`), which a render front-end dispatches
 * task painting on.
 */
export type TaskRendererPluginId =
  (typeof TASK_RENDERER_PLUGIN_IDS)[number];

/** Canonical renderer ids for calendar/event rows across sheet slots. */
export const CALENDAR_EVENT_RENDERER_PLUGIN_IDS = [
  "timeline[row].event",
  "timeline[column].event",
  "grid.event",
] as const;
deepFrozen(CALENDAR_EVENT_RENDERER_PLUGIN_IDS);

/**
 * One of the calendar-event renderer ids (`CALENDAR_EVENT_RENDERER_PLUGIN_IDS`), which a render
 * front-end dispatches event painting on.
 */
export type CalendarEventRendererPluginId =
  (typeof CALENDAR_EVENT_RENDERER_PLUGIN_IDS)[number];

/** Registry shape needed by the shared binding resolver. */
export interface RenderPluginLookup {
  get(id: string, role: Role): RenderPluginSpec;
}

/** Pure row-to-plugin binding result, independent of DOM or terminal output. */
export interface RenderBinding {
  readonly surface: RenderBindingSurface;
  readonly requestedRowId: RowLocalId;
  readonly pluginRowId: RowLocalId;
  readonly sourceRowId: Option<RowLocalId>;
  readonly pluginId: string;
  readonly pluginRole: Role;
  readonly rowRole: Role;
  readonly contentType: string;
  readonly bindingKind: RenderBindingKind;
  readonly boundRowId: Option<RowLocalId>;
  readonly boundRendererId: Option<RendererId | RetiredRendererType>;
}

/** All plugin bindings relevant for one visual row. */
export interface RenderBindings {
  readonly row: RenderBinding;
  readonly cell: RenderBinding;
  readonly chrome: RenderBinding;
  readonly capabilities: readonly RenderBinding[];
}

/** Minimal shared render-plugin registry for pure consumers. */
export class RenderPluginRegistry implements RenderPluginLookup {
  #specs = new Map<string, RenderPluginSpec>();

  plugin(spec: RenderPluginSpec): this {
    assertNonEmptyString(spec.id, "RenderPluginRegistry.plugin spec.id");
    for (const id of renderPluginLookupIdsForSpec(spec)) {
      this.#specs.set(renderPluginMapKey(id, spec.role), spec);
    }
    return this;
  }

  get(id: string, role: Role): RenderPluginSpec {
    assertNonEmptyString(id, `RenderPluginRegistry.get(${role}) id`);
    const normalized = normalizeRenderPluginId(id);
    const existing = this.#specs.get(renderPluginMapKey(id, role)) ??
      this.#specs.get(renderPluginMapKey(normalized, role));
    if (existing) return existing;
    const spec = makeDefaultRenderPluginSpec(normalized, role);
    this.plugin(spec);
    return spec;
  }
}

/** Create a default spec for ids outside the known catalog. */
function makeDefaultRenderPluginSpec(
  id: string,
  role: Role,
): RenderPluginSpec {
  assertNonEmptyString(id, `makeDefaultRenderPluginSpec(${role}) id`);
  return {
    id,
    role,
    aliases: renderPluginAliasesForId(id),
  };
}

/**
 * All compatibility ids a render-plugin registration should answer.
 *
 * Adapters still own their concrete plugin objects, but registration
 * identity is shared here so CLI and DOM cannot drift on aliases.
 */
function renderPluginLookupIdsForSpec(
  spec: Pick<RenderPluginSpec, "id" | "role" | "aliases">,
): readonly string[] {
  assertNonEmptyString(spec.id, "renderPluginLookupIdsForSpec spec.id");
  const ids: string[] = [];
  const seen = new Set<string>();
  const push = (id: string, label: string) => {
    assertNonEmptyString(id, label);
    if (!seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
    const normalized = normalizeRenderPluginId(id);
    if (seen.has(normalized)) return;
    seen.add(normalized);
    ids.push(normalized);
  };

  push(spec.id, "renderPluginLookupIdsForSpec id");
  for (const alias of spec.aliases ?? []) {
    push(alias, "renderPluginLookupIdsForSpec alias");
  }
  for (const alias of renderPluginAliasesForId(spec.id)) {
    push(alias, "renderPluginLookupIdsForSpec shared alias");
  }
  return ids;
}

/** Resolve every plugin surface relevant to one hydrated rows-side row. */
function resolveRenderBindings(
  row: ModelRow,
  registry: RenderPluginLookup,
): RenderBindings {
  const own = resolveRowRenderBinding(row, registry);
  const cell = resolveCellRenderBinding(row, registry);
  const chrome = resolveChromeRenderBinding(row, registry);
  return {
    row: own,
    cell,
    chrome,
    capabilities: uniqueBindings([chrome, cell, own]),
  };
}

/**
 * Resolve the plugin attached to the row's own descriptor.
 *
 * This is the polymorphic descriptor binding: alias rows bind by
 * template id, layout rows by layout id, renderer rows by renderer id,
 * and source rows by source descriptor type. It deliberately does not
 * read legacy `row.type`.
 */
function resolveRowRenderBinding(
  row: ModelRow,
  registry: RenderPluginLookup,
): RenderBinding {
  const key = renderPluginKeyForRow(row);
  const plugin = registry.get(key.id, key.role);
  const bindingKind = row instanceof AliasRow
    ? "alias-template"
    : row instanceof RendererRow
    ? "renderer-row"
    : row instanceof LayoutRow
    ? "layout-row"
    : "row-descriptor";
  return makeRenderBinding({
    row,
    surface: "row",
    pluginId: plugin.id,
    pluginRole: plugin.role,
    contentType: key.id,
    bindingKind,
    pluginRow: row,
    sourceRow: row instanceof SourceRow ? row : null,
    boundRendererId: row instanceof RendererRow
      ? row.descriptor.rendererId
      : null,
  });
}

/**
 * Resolve the plugin that paints a row's cell/body.
 *
 * Source rows paint through their default renderer child. Alias rows
 * paint through their first hydrated cell-bindable descendant. Layout
 * and renderer rows paint through their own descriptor plugin.
 */
function resolveCellRenderBinding(
  row: ModelRow,
  registry: RenderPluginLookup,
): RenderBinding {
  const role = renderPluginRoleForRow(row);
  if (row instanceof SourceRow) {
    const renderer = firstRendererChild(row);
    if (renderer) {
      const rendererId = renderer.descriptor.rendererId;
      const plugin = registry.get(rendererId, "renderer");
      if (shapeGateAllowsSourcePlugin(row, plugin)) {
        return makeRenderBinding({
          row,
          surface: "cell",
          pluginId: plugin.id,
          pluginRole: plugin.role,
          contentType: rendererId,
          bindingKind: "source-default-renderer",
          pluginRow: renderer,
          sourceRow: row,
          boundRowId: renderer.id,
          boundRendererId: rendererId,
        });
      }
    }

    const rendererOverride = row.descriptor.rendererOverride;
    if (rendererOverride !== undefined) {
      const plugin = registry.get(rendererOverride, "renderer");
      if (shapeGateAllowsSourcePlugin(row, plugin)) {
        return makeRenderBinding({
          row,
          surface: "cell",
          pluginId: plugin.id,
          pluginRole: plugin.role,
          contentType: rendererOverride,
          bindingKind: "source-renderer-override",
          pluginRow: row,
          sourceRow: row,
          boundRowId: null,
          boundRendererId: rendererOverride,
        });
      }
    }

    // No materialized renderer child and no override. Shaped sources now
    // materialize their renderer as a child (resolved above via
    // `firstRendererChild`), so anything reaching here is a shapeless source
    // → the generic `column.data-json` fallback. The former render-time
    // `meta.shape → renderer` hint is retired; shape resolution lives in
    // `resolveDefaultRendererId` (hydrate time), keeping the tree the single
    // source of truth.
    return sourceFallbackBinding(row, registry, "source-fallback");
  }

  if (row instanceof RendererRow) {
    const rendererId = row.descriptor.rendererId;
    const plugin = registry.get(rendererId, "renderer");
    return makeRenderBinding({
      row,
      surface: "cell",
      pluginId: plugin.id,
      pluginRole: plugin.role,
      contentType: rendererId,
      bindingKind: "renderer-row",
      pluginRow: row,
      sourceRow: row.parent instanceof SourceRow ? row.parent : null,
      boundRowId: row.id,
      boundRendererId: rendererId,
    });
  }

  if (row instanceof AliasRow) {
    const descendant = firstCellBindableDescendant(row);
    if (descendant) {
      return retargetBinding(
        resolveCellRenderBinding(descendant, registry),
        row,
        "cell",
        "alias-cell-descendant",
      );
    }
    throw new RenderBinding.NotCellBindableError(
      `resolveCellRenderBinding: alias row ${row.id.rawId} ` +
        `(${row.descriptor.template}) has no cell-bindable descendant.`,
    );
  }

  const plugin = registry.get(descriptorTypeForRow(row), role);
  return makeRenderBinding({
    row,
    surface: "cell",
    pluginId: plugin.id,
    pluginRole: plugin.role,
    contentType: descriptorTypeForRow(row),
    bindingKind: "layout-row",
    pluginRow: row,
    sourceRow: null,
    boundRowId: null,
    boundRendererId: null,
  });
}

/**
 * Resolve the plugin that owns row chrome: legends, row options,
 * and row-level affordances. Alias and source rows own their chrome;
 * source cells still paint through their renderer binding.
 */
function resolveChromeRenderBinding(
  row: ModelRow,
  registry: RenderPluginLookup,
): RenderBinding {
  if (row instanceof AliasRow || row instanceof SourceRow) {
    return withSurface(resolveRowRenderBinding(row, registry), "chrome");
  }
  return withSurface(resolveCellRenderBinding(row, registry), "chrome");
}

/** Compatibility alias: resolve the cell/body render binding. */
function resolveRenderBinding(
  row: ModelRow,
  registry: RenderPluginLookup,
): RenderBinding {
  return resolveCellRenderBinding(row, registry);
}

/** Map a hydrated row instance to its render-plugin role. */
function renderPluginRoleForRow(row: ModelRow): Role {
  if (row instanceof SourceRow) return "source";
  if (row instanceof RendererRow) return "renderer";
  if (row instanceof AliasRow) return "alias";
  if (row instanceof InertRow) return "inert";
  return "layout";
}

/** Canonical descriptor identity, distinct from legacy server row type. */
function descriptorTypeForRow(row: ModelRow): string {
  return descriptorType(row.descriptor);
}

/** Canonical descriptor identity for a descriptor value. */
function descriptorType(descriptor: Descriptor): string {
  switch (descriptor.role) {
    case "source":
      return [
        "source",
        descriptor.dtype,
        descriptor.sourceShape,
        descriptor.cellCardinality,
      ].join(".");
    case "renderer":
      return descriptor.rendererId;
    case "layout":
      return descriptor.layout;
    case "inert":
      return descriptor.kind;
    case "alias":
      return descriptor.template;
  }
}

/** Role-qualified plugin key for a descriptor. */
function renderPluginKeyForDescriptor(
  descriptor: Descriptor,
): RenderPluginRef {
  return {
    id: descriptorType(descriptor),
    role: descriptor.role,
  };
}

/** Role-qualified plugin key for a row's own descriptor. */
function renderPluginKeyForRow(row: ModelRow): RenderPluginRef {
  return renderPluginKeyForDescriptor(row.descriptor);
}

/**
 * Compatibility normalization for plugin ids shared by CLI and DOM.
 *
 * Plugin ids are canonical (the row-kind migration removed the legacy
 * spellings), so normalization is intentionally an identity — not a
 * translation table.
 */
function normalizeRenderPluginId(id: string): string {
  return id;
}

/** Compatibility aliases for canonical plugin ids. */
function renderPluginAliasesForId(id: string): readonly string[] {
  assertNonEmptyString(id, "renderPluginAliasesForId id");
  return [];
}

function firstRendererChild(source: SourceRow): Option<RendererRow> {
  for (const child of source.children) {
    if (child instanceof RendererRow) return child;
  }
  return null;
}

function firstCellBindableDescendant(row: ModelRow): Option<ModelRow> {
  // Layout children are explicit presentation surfaces. Prefer them
  // over compatibility source children on aliases such as `calendar`,
  // whose visible row should bind through `timeline[column]` / `grid`
  // while keeping alias chrome at the top level.
  const layoutChild = firstDirectLayoutChild(row);
  if (layoutChild) return layoutChild;

  for (const child of row.children) {
    if (child instanceof AliasRow) {
      const descendant = firstCellBindableDescendant(child);
      if (descendant) return descendant;
      continue;
    }
    return child;
  }
  return null;
}

function sourceFallbackBinding(
  row: SourceRow,
  registry: RenderPluginLookup,
  bindingKind: Extract<RenderBindingKind, "source-fallback">,
): RenderBinding {
  const rendererId: RendererId = "column.data-json";
  const plugin = registry.get(rendererId, "renderer");
  return makeRenderBinding({
    row,
    surface: "cell",
    pluginId: plugin.id,
    pluginRole: plugin.role,
    contentType: descriptorTypeForRow(row),
    bindingKind,
    pluginRow: row,
    sourceRow: row,
    boundRowId: null,
    boundRendererId: rendererId,
  });
}

function shapeGateAllowsSourcePlugin(
  row: SourceRow,
  plugin: RenderPluginSpec,
): boolean {
  const decision = checkRenderPluginShape(plugin, shapeForSourceRow(row));
  if (decision.accepted) return true;
  if (decision.onMismatch === "error") {
    const actual = decision.actual === null ? "no shape" : decision.actual;
    throw new Error(
      `render plugin "${plugin.id}" expected shape ${decision.expected.join(", ")} ` +
        `but row "${row.id}" has ${actual}`,
    );
  }
  return decision.onMismatch === "warn";
}

function shapeForSourceRow(row: SourceRow): Option<string> {
  const meta = row.sheetReads.rowMeta(row.id).meta;
  if (!meta) return null;
  const shape = meta.shape;
  return typeof shape === "string" ? shape : null;
}

function firstDirectLayoutChild(row: ModelRow): Option<LayoutRow> {
  for (const child of row.children) {
    if (child instanceof LayoutRow) return child;
  }
  return null;
}

function makeRenderBinding(props: {
  row: ModelRow;
  surface: RenderBindingSurface;
  pluginId: string;
  pluginRole: Role;
  contentType: string;
  bindingKind: RenderBindingKind;
  pluginRow: ModelRow;
  sourceRow: Option<SourceRow>;
  boundRowId?: Option<RowLocalId>;
  boundRendererId: Option<RendererId | RetiredRendererType>;
}): RenderBinding {
  assertNonEmptyString(props.pluginId, "makeRenderBinding pluginId");
  assertNonEmptyString(props.contentType, "makeRenderBinding contentType");
  return {
    surface: props.surface,
    requestedRowId: props.row.id,
    pluginRowId: props.pluginRow.id,
    sourceRowId: props.sourceRow?.id ?? null,
    pluginId: props.pluginId,
    pluginRole: props.pluginRole,
    rowRole: renderPluginRoleForRow(props.row),
    contentType: props.contentType,
    bindingKind: props.bindingKind,
    boundRowId: props.boundRowId ?? null,
    boundRendererId: props.boundRendererId,
  };
}

function retargetBinding(
  binding: RenderBinding,
  requestedRow: ModelRow,
  surface: RenderBindingSurface,
  bindingKind: RenderBindingKind,
): RenderBinding {
  return {
    ...binding,
    surface,
    requestedRowId: requestedRow.id,
    rowRole: renderPluginRoleForRow(requestedRow),
    bindingKind,
  };
}

function withSurface(
  binding: RenderBinding,
  surface: RenderBindingSurface,
): RenderBinding {
  return { ...binding, surface };
}

function uniqueBindings(bindings: readonly RenderBinding[]): readonly RenderBinding[] {
  const result: RenderBinding[] = [];
  const seen = new Set<string>();
  for (const binding of bindings) {
    const key = [
      binding.pluginRole,
      binding.pluginId,
      binding.pluginRowId.rawId,
    ].join("\n");
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(binding);
  }
  return result;
}

function renderPluginMapKey(id: string, role: Role): string {
  assertNonEmptyString(id, `renderPluginMapKey(${role}) id`);
  return `${role}:${id}`;
}

function assertNonEmptyString(value: string, label: string): void {
  if (value.length > 0) return;
  throw new Error(`${label} must be a non-empty string.`);
}

// ── Namespaces ─────────────────────────────────────────────────────

const $descriptorType = descriptorType;
const $descriptorTypeForRow = descriptorTypeForRow;

/**
 * Values that go with `RenderPluginSpec`: `checkShape`, `makeDefault`,
 * `lookupIds`, `normalizeId`, `aliasesFor`, `keyForDescriptor`, and 5 more.
 */
export namespace RenderPluginSpec {
  export const checkShape = checkRenderPluginShape;
  export const makeDefault = makeDefaultRenderPluginSpec;
  export const lookupIds = renderPluginLookupIdsForSpec;
  export const normalizeId = normalizeRenderPluginId;
  export const aliasesFor = renderPluginAliasesForId;
  export const keyForDescriptor = renderPluginKeyForDescriptor;
  export const keyForRow = renderPluginKeyForRow;
  export const mapKey = renderPluginMapKey;
  export const roleForRow = renderPluginRoleForRow;
  export const descriptorType: (descriptor: Descriptor) => string = $descriptorType;
  export const descriptorTypeForRow: (row: ModelRow) => string = $descriptorTypeForRow;
}

/**
 * Values that go with `RenderBinding`: `resolveAll`, `resolveRow`,
 * `resolveCell`, `resolveChrome`, `resolve`, `NotCellBindableError`, and 1
 * more.
 */
export namespace RenderBinding {
  export const resolveAll = resolveRenderBindings;
  export const resolveRow = resolveRowRenderBinding;
  export const resolveCell = resolveCellRenderBinding;
  export const resolveChrome = resolveChromeRenderBinding;
  export const resolve = resolveRenderBinding;

  /**
   * Thrown by cell-binding resolution when a row cannot be cell-bound right
   * now: an alias whose first cell-bindable descendant is transiently
   * un-materialized (mid alias re-expansion), or an alias plugin asked to
   * paint a cell directly. This is RECOVERABLE — a render/paint caller skips
   * the row this pass and the next rebind resolves it; it MUST NOT escape
   * into the scroll bind drain, where an uncaught throw aborts the batch and
   * strands every later column with an empty `cid`. Detect it with
   * `isNotCellBindable`, which matches a stable `code` rather than the
   * human-readable message (free to reword) and avoids `instanceof` across
   * bundle seams.
   */
  export class NotCellBindableError extends Error {
    static readonly CODE = "render-binding/not-cell-bindable";
    readonly code: string = NotCellBindableError.CODE;
    constructor(message: string) {
      super(message);
      this.name = "NotCellBindableError";
    }
  }

  /**
   * True iff `error` is a {@link NotCellBindableError}. Checks the stable
   * `code` discriminant (not message text, not subclass `instanceof`) so it
   * survives message rewording and module duplication across bundles.
   */
  export function isNotCellBindable(error: unknown): boolean {
    return (
      error instanceof Error &&
      "code" in error &&
      error.code === NotCellBindableError.CODE
    );
  }
}
